import { Button, Form, Input } from 'antd';
// import { ModalWrap } from '../../../components/modal-wrap';
import { ModalWrap } from '../../component/modal-wrap';
import React, { useState, useEffect } from 'react';
import { createStudio, fetchGameList } from '../../../api';
import styles from './index.module.scss';
import { formatStudioList } from '../lib/studio-data';
import { validateNameLength } from '../../../lib';
import { IAPIGameItem } from '../../../model/api-modules';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';

interface IProps {
  readonly onClose: () => void;
  readonly onReload: () => Promise<void>;
  readonly onlyShowForm?: boolean;
  readonly onlyShowFormNode?: React.ReactNode;
  readonly onToStudio: (studio: IAPIGameItem) => Promise<void>;
}

export const CreateStudioModal = (props: IProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const head: HTMLElement | null =
      document.querySelector('.gc-page-headline');
    if (head) {
      head.style.zIndex = '100';
    }
  }, []);

  const onClose = () => {
    const head: HTMLElement | null =
      document.querySelector('.gc-page-headline');
    if (head) {
      head.style.zIndex = '999';
    }
    props.onClose();
  };

  const getStudioList = async () => {
    const res = await fetchGameList();
    if (res) {
      const currentList = formatStudioList(res);
      currentList.sort(
        (a, b) =>
          new Date(a.studioCreateTime || 0).getTime() -
          new Date(b.studioCreateTime || 0).getTime()
      );
      return {
        newStudioId:
          currentList.length !== 0 ? currentList.slice(-1)[0]?.studioID : '',
        list: currentList,
      };
    }
    return null;
  };

  const toCreateStudio = async () => {
    const result = await getStudioList();
    const { newStudioId = '', list = [] } = result || {};
    if (list?.length && newStudioId) {
      const it = list.find((v) => v.studioID === newStudioId);
      if (it) {
        messageSuccess(getFormatMessage('create_studio_success'));
        await props.onReload();
        await props.onToStudio(it);
        onClose();
        return true;
      }
      return false;
    }
    return false;
  };

  const createNewStudio = async (studioName: string) => {
    try {
      const res = await createStudio(studioName);
      if (res) {
        if (res.maxLimit) {
          messageWarning(
            getFormatMessage('maximum_max_limit_characters', {
              params: {
                maxLimit: res?.maxLimit,
              },
            })
          );
          return false;
        }
        return await toCreateStudio();
      }
      messageError(getFormatMessage('create_studio_fail'));
      return false;
    } catch (err: any) {
      messageError(err?.apiError?.message);
      return false;
    }
  };

  const onOk = async () => {
    try {
      const value = await form.validateFields();
      setLoading(true);
      const res = await createNewStudio(value.StudioName.trim());
      setLoading(false);
      return res;
    } catch (error) {
      return false;
    }
  };

  const renderForm = () => {
    return (
      <Form
        form={form}
        className="c-main-form"
        name="basic"
        initialValues={{ remember: true }}
        autoComplete="off"
        layout="vertical"
        colon={false}
        requiredMark={false}
      >
        <Form.Item
          label={getFormatMessage('studio_name_uppercase')}
          name="StudioName"
          rules={[
            {
              required: true,
              validator: validateNameLength(
                getFormatMessage('studio_name_lowercase')
              ),
            },
          ]}
        >
          <Input
            autoFocus
            className="c-antd-input-xl-main"
            placeholder={getFormatMessage('please_enter_your_studio_name')}
            spellCheck="false"
          />
        </Form.Item>
      </Form>
    );
  };

  const renderCustomSubmitButton = () => {
    return (
      <div className={styles.bottomBox}>
        <Button
          type="primary"
          loading={loading}
          block
          onClick={onOk}
          className="in-game-ant-button-xxl"
        >
          {getFormatMessage('create_now')}
        </Button>
        {props.onlyShowFormNode}
      </div>
    );
  };

  if (props.onlyShowForm) {
    return (
      <div className={styles.formBox}>
        {renderForm()}
        {renderCustomSubmitButton()}
      </div>
    );
  }

  return (
    <ModalWrap
      loading={loading}
      onOk={onOk}
      oKText={getFormatMessage('create')}
      cancelText={getFormatMessage('cancel')}
      width="480px"
      onClose={onClose}
      title={getFormatMessage('new_studio')}
      className="applyJoinModal"
      // maskClosable={false}
    >
      {renderForm()}
    </ModalWrap>
  );
};
